import { capitalize } from "@common/helpers/string";
import { EntityProfileType } from "../../../types/entity";
import {
    FinancialAccountBase,
    FinancialAccountType,
} from "../../../types/financialAccount";

import { AccountRole } from "../../../types/domains/accounting/account";
import { FinancialAccountsGroup } from "../../../types/domains/accounting/chart-of-accounts";
import {
    accountTypeToAccountClass,
    isCorporationLedger,
    isPartnershipLedger,
    isSolePropLedger,
} from "./helpers";
import {
    AccountClass,
    AccountSubtype,
    AccountType,
    DefaultCashBasisAccountCodes,
} from "./accounts";

export enum SolePropEquityAccountCodes {
    OwnerInvestments = 300000,
    OwnerDraws = 301000,
    OwnerCapital = 302000,
}

export enum SCorpAccountCodes {
    LoansToShareholders = 190200,

    LoansFromShareholders = 250100,

    CommonStock = 300000,
    AdditionalPaidInCapital = 301000,
    Contributions = 302000,
    Distributions = 303000,
    Dividends = 304000,
    RetainedEarnings = 305000,
    AdjustmentsToShareholderEquity = 306000,
    CostOfTreasuryStock = 307000,

    OfficersCompensation = 600200,
}

export enum PartnershipAccountCodes {
    LoansToPartners = 190200,

    LoansFromPartners = 250100,

    Contributions = 300000,
    Distributions = 301000,
    Capital = 302000,

    GuaranteedPaymentsToPartners = 600200,
}

export type AccountCodes =
    | DefaultCashBasisAccountCodes
    | SolePropEquityAccountCodes
    | SCorpAccountCodes
    | PartnershipAccountCodes;

export const defaultSolePropEquityChartOfAccounts: ChartOfAccounts<SolePropEquityAccountCodes> =
    {
        300000: {
            name: "Owner Investments",
            type: AccountType.equity,
            subtype: AccountSubtype.contributions,
        },
        301000: {
            name: "Owner Draws",
            type: AccountType.equity,
            subtype: AccountSubtype.distributions,
        },
        302000: {
            name: "Owner Capital",
            type: AccountType.equity,
            subtype: AccountSubtype.capital,
        },
    };

export const defaultSCorpEquityChartOfAccounts: Partial<
    ChartOfAccounts<SCorpAccountCodes>
> = {
    300000: {
        name: "Common Stock",
        type: AccountType.equity,
        subtype: AccountSubtype.commonStock,
    },
    301000: {
        name: "Additional Paid-In Capital",
        type: AccountType.equity,
        subtype: AccountSubtype.additionalPaidInCapital,
    },
    302000: {
        name: "Contributions",
        type: AccountType.equity,
        subtype: AccountSubtype.contributions,
    },
    303000: {
        name: "Distributions",
        type: AccountType.equity,
        subtype: AccountSubtype.distributions,
    },
    304000: {
        name: "Dividends",
        type: AccountType.equity,
        subtype: AccountSubtype.dividends,
    },
    305000: {
        name: "Retained Earnings",
        type: AccountType.equity,
        subtype: AccountSubtype.retainedEarnings,
    },
    306000: {
        name: "Adjustments to Shareholders' Equity",
        type: AccountType.equity,
        subtype: AccountSubtype.adjustmentsToShareholdersEquity,
    },
    307000: {
        name: "Cost of Treasury Stock",
        type: AccountType.equity,
        subtype: AccountSubtype.costOfTreasuryStock,
    },
};

export const defaultPartnershipEquityChartOfAccounts: ChartOfAccounts<SolePropEquityAccountCodes> =
    {
        300000: {
            name: "Contributions",
            type: AccountType.equity,
            subtype: AccountSubtype.contributions,
        },
        301000: {
            name: "Distributions",
            type: AccountType.equity,
            subtype: AccountSubtype.distributions,
        },
        302000: {
            name: "Capital",
            type: AccountType.equity,
            subtype: AccountSubtype.capital,
        },
    };

// NOTE: legacy structure, to replace with defaultCashChartOfAccounts
export const defaultCashChartOfAccountsOld: Partial<
    ChartOfAccounts<DefaultCashBasisAccountCodes>
> = {
    100000: {
        name: "Cash on Hand",
        type: AccountType.cash,
        subtype: AccountSubtype.cashOnHand,
    },
    109900: {
        name: "Bank Transfer Clearing",
        type: AccountType.cash,
        subtype: AccountSubtype.transferClearing,
    },
    109901: {
        name: "Payment Transfer Clearing",
        type: AccountType.cash,
        subtype: AccountSubtype.transferClearing,
    },
    110000: {
        name: "Accounts Receivable",
        type: AccountType.accountsReceivable,
        subtype: AccountSubtype.accountsReceivable,
    },
    119900: {
        name: "Allowance for Bad Debts",
        type: AccountType.accountsReceivable,
        subtype: AccountSubtype.allowanceForBadDebts,
    },
    120000: {
        name: "Inventory",
        type: AccountType.inventory,
        subtype: AccountSubtype.inventory,
    },
    130000: {
        name: "Prepaid Expenses",
        type: AccountType.otherCurrentAssets,
        subtype: AccountSubtype.prepaidExpenses,
    },
    130100: {
        name: "Accrued Revenue",
        type: AccountType.otherCurrentAssets,
        subtype: AccountSubtype.accruedRevenue,
    },
    139900: {
        name: "Other Current Assets",
        type: AccountType.otherCurrentAssets,
        subtype: AccountSubtype.otherCurrentAssets,
    },
    140000: {
        name: "U.S. Government Obligations",
        type: AccountType.investments,
        subtype: AccountSubtype.usGovernmentObligations,
    },
    140100: {
        name: "Tax-Exempt Securities",
        type: AccountType.investments,
        subtype: AccountSubtype.taxExemptSecurities,
    },
    140200: {
        name: "Investments",
        type: AccountType.investments,
        subtype: AccountSubtype.investments,
    },
    150000: {
        name: "Land",
        type: AccountType.fixedAssets,
        subtype: AccountSubtype.land,
    },
    150100: {
        name: "Buildings",
        type: AccountType.fixedAssets,
        subtype: AccountSubtype.buildings,
    },
    150200: {
        name: "Leasehold Improvements",
        type: AccountType.fixedAssets,
        subtype: AccountSubtype.leaseholds,
    },
    150300: {
        name: "Vehicles",
        type: AccountType.fixedAssets,
        subtype: AccountSubtype.vehicles,
    },
    150400: {
        name: "Equipment",
        type: AccountType.fixedAssets,
        subtype: AccountSubtype.equipment,
    },
    150500: {
        name: "Furniture and Fixtures",
        type: AccountType.fixedAssets,
        subtype: AccountSubtype.furnitureAndFixtures,
    },
    159800: {
        name: "Other Fixed Assets",
        type: AccountType.fixedAssets,
        subtype: AccountSubtype.otherFixedAssets,
    },
    159900: {
        name: "Accumulated Depreciation",
        type: AccountType.fixedAssets,
        subtype: AccountSubtype.accumulatedDepreciation,
    },
    160000: {
        name: "Startup Costs",
        type: AccountType.intangibleAssets,
        subtype: AccountSubtype.startupCosts,
    },
    160100: {
        name: "Trademarks and Patents",
        type: AccountType.intangibleAssets,
        subtype: AccountSubtype.trademarksAndPatents,
    },
    160200: {
        name: "Goodwill",
        type: AccountType.intangibleAssets,
        subtype: AccountSubtype.goodwill,
    },
    169800: {
        name: "Other Intangible Assets",
        type: AccountType.intangibleAssets,
        subtype: AccountSubtype.otherIntangibleAssets,
    },
    169900: {
        name: "Accumulated Amortization",
        type: AccountType.intangibleAssets,
        subtype: AccountSubtype.accumulatedAmortization,
    },
    200000: {
        name: "Accounts Payable",
        type: AccountType.accountsPayable,
        subtype: AccountSubtype.accountsPayable,
    },
    219900: {
        name: "Credit Card Clearing",
        type: AccountType.creditCards,
        subtype: AccountSubtype.creditCardClearing,
    },
    220000: {
        name: "Payroll Payable",
        type: AccountType.payrollLiabilities,
        subtype: AccountSubtype.payrollPayable,
    },
    230000: {
        name: "Line of Credit",
        type: AccountType.shortTermLoans,
        subtype: AccountSubtype.lineOfCredit,
    },
    230100: {
        name: "Short-Term Loans",
        type: AccountType.shortTermLoans,
        subtype: AccountSubtype.shortTermLoans,
    },
    239900: {
        name: "Current Maturities of Long-Term Debt",
        type: AccountType.shortTermLoans,
        subtype: AccountSubtype.currentMaturitiesOfLongTermDebt,
    },
    240000: {
        name: "Sales Tax Payable",
        type: AccountType.otherCurrentLiabilities,
        subtype: AccountSubtype.salesTaxPayable,
    },
    240100: {
        name: "Deferred Revenue",
        type: AccountType.otherCurrentLiabilities,
        subtype: AccountSubtype.deferredRevenue,
    },
    240200: {
        name: "Accrued Expenses",
        type: AccountType.otherCurrentLiabilities,
        subtype: AccountSubtype.accruedExpenses,
    },
    240300: {
        name: "Customer Deposits",
        type: AccountType.otherCurrentLiabilities,
        subtype: AccountSubtype.customerDeposits,
    },
    249900: {
        name: "Other Current Liabilities",
        type: AccountType.otherCurrentLiabilities,
        subtype: AccountSubtype.otherCurrentLiabilities,
    },
    290000: {
        name: "Intercompany Payables",
        type: AccountType.otherLiabilities,
        subtype: AccountSubtype.intercompanyPayables,
    },
    299900: {
        name: "Other Long-Term Liabilities",
        type: AccountType.otherLiabilities,
        subtype: AccountSubtype.otherLiabilities,
    },
    400000: {
        name: "Revenue",
        type: AccountType.income,
        subtype: AccountSubtype.revenue,
    },
    401000: {
        name: "Refunds and Returns",
        type: AccountType.income,
        subtype: AccountSubtype.refundsAndReturns,
    },
    402000: {
        name: "Discounts and Allowances",
        type: AccountType.income,
        subtype: AccountSubtype.discountsAndAllowances,
    },
    499099: {
        name: "Uncategorized Income",
        type: AccountType.income,
        subtype: AccountSubtype.uncategorizedIncome,
    },
    500000: {
        name: "COGS - Goods and Materials",
        type: AccountType.cogs,
        subtype: AccountSubtype.cogsGoodsAndMaterials,
    },
    500100: {
        name: "COGS - Labor",
        type: AccountType.cogs,
        subtype: AccountSubtype.cogsLabor,
    },
    500200: {
        name: "COGS - Other",
        type: AccountType.cogs,
        subtype: AccountSubtype.cogsOther,
    },
    610000: {
        name: "Payroll Taxes",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.payrollTaxes,
    },
    620000: {
        name: "Employee Benefits",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.employeeBenefits,
    },
    620100: {
        name: "Retirement Benefits",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.retirementBenefits,
    },
    700000: {
        name: "Advertising and Marketing",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.advertisingAndMarketing,
    },
    701000: {
        name: "Learning and Development",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.learningAndDevelopment,
    },
    702000: {
        name: "Licenses and Permits",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.licensesAndPermits,
    },
    703000: {
        name: "Property Taxes",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.propertyTaxes,
    },
    704000: {
        name: "Entertainment",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.entertainment,
    },
    705000: {
        name: "Insurance - Business",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.insurance,
    },
    705001: {
        name: (entityType: EntityProfileType) => {
            let name = "Insurance - ";
            if (isSolePropLedger(entityType)) {
                name += "Owner";
            }
            if (isCorporationLedger(entityType)) {
                name += "Officers";
            }
            if (isPartnershipLedger(entityType)) {
                name += "Partners";
            }
            return name;
        },
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.insurance,
    },
    706000: {
        name: "Internet and Phone",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.internetAndPhone,
    },
    707000: {
        name: "Facilities - Repairs and Maintenance",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.facilities,
    },
    707001: {
        name: "Facilities - Cleaning Fees",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.facilities,
    },
    707002: {
        name: "Facilities - Management Fees",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.facilities,
    },
    708000: {
        name: "Furniture",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.furnitureAndFixtures,
    },
    709000: {
        name: "Meals",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.meals,
    },
    709001: {
        name: "Meals - Employee Catering",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.meals,
    },
    710000: {
        name: "Merchant and Bank Fees",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.merchantAndBankFees,
    },
    711000: {
        name: "Supplies and Equipment",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.suppliesAndEquipment,
    },
    712000: {
        name: "Software and Subscriptions",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.softwareAndSubscriptions,
    },
    713000: {
        name: "Office Expenses",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.officeExpenses,
    },
    714000: {
        name: "Office Utilities",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.officeUtilities,
    },
    715000: {
        name: "Legal and Professional Fees",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.legalAndProfessionalFees,
    },
    716000: {
        name: "Rent and Lease",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.rentAndLease,
    },
    717000: {
        name: "Travel",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.travel,
    },
    718000: {
        name: "Vehicle",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.vehicle,
    },
    718001: {
        name: "Vehicle - Leases",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.vehicle,
    },
    718002: {
        name: "Vehicle - Parking and Tolls",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.vehicle,
    },
    719000: {
        name: "Loan Fees",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.loanFees,
    },
    799000: {
        name: "Other Operating Expenses",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.otherOperatingExpenses,
    },
    799099: {
        name: "Uncategorized Expenses",
        type: AccountType.operatingExpenses,
        subtype: AccountSubtype.uncategorizedExpenses,
    },
    800000: {
        name: "Rental Income",
        type: AccountType.otherIncome,
        subtype: AccountSubtype.rentalIncome,
    },
    801000: {
        name: "Interest Income",
        type: AccountType.otherIncome,
        subtype: AccountSubtype.interestIncome,
    },
    802000: {
        name: "Dividend Income",
        type: AccountType.otherIncome,
        subtype: AccountSubtype.dividendIncome,
    },
    803000: {
        name: "Rebate Income",
        type: AccountType.otherIncome,
        subtype: AccountSubtype.rebateIncome,
    },
    804000: {
        name: "Royalty Income",
        type: AccountType.otherIncome,
        subtype: AccountSubtype.royaltyIncome,
    },
    848000: {
        name: "Gain (Loss) Asset Disposals",
        type: AccountType.otherIncome,
        subtype: AccountSubtype.gainLossAssetDisposals,
    },
    849000: {
        name: "Other Income",
        type: AccountType.otherIncome,
        subtype: AccountSubtype.otherIncome,
    },
    850000: {
        name: "Interest Expense",
        type: AccountType.otherExpenses,
        subtype: AccountSubtype.interestExpense,
    },
    851000: {
        name: "Depreciation",
        type: AccountType.otherExpenses,
        subtype: AccountSubtype.depreciation,
    },
    852000: {
        name: "Amortization",
        type: AccountType.otherExpenses,
        subtype: AccountSubtype.amortization,
    },
    853000: {
        name: "Depletion",
        type: AccountType.otherExpenses,
        subtype: AccountSubtype.depletion,
    },
    854000: {
        name: "Fines and Penalties",
        type: AccountType.otherExpenses,
        subtype: AccountSubtype.finesAndPenalties,
    },
    855000: {
        name: "Bad Debts",
        type: AccountType.otherExpenses,
        subtype: AccountSubtype.badDebts,
    },
    856000: {
        name: "Donations",
        type: AccountType.otherExpenses,
        subtype: AccountSubtype.donations,
    },
    899000: {
        name: "Other Expense",
        type: AccountType.otherExpenses,
        subtype: AccountSubtype.otherExpense,
    },
    900000: {
        name: "Federal Taxes",
        type: AccountType.otherExpenses,
        subtype: AccountSubtype.federalTaxes,
    },
    900100: {
        name: "State and Local Taxes",
        type: AccountType.otherExpenses,
        subtype: AccountSubtype.stateAndLocalTaxes,
    },
};

// DEPRECATED, use defaultChartOfAccountsNew
// NOTE: at the end of the day we need to migrate all account declarations
// to this format and also use account type keys instead of account codes
// to support taxonomy mapping for accounts from dynamic types
export const ChartOfAccountsTypesWithDynamicNumbering: ChartOfAccountsTypesWithDynamicNumberingType =
    [
        {
            typeKey: AccountType.otherAssets,
            startFrom: 190000,
            upTo: 199999,
            // for sections starting counting from the end
            endFrom: 199900,
            offset: 100,
            subtypesStartFromBeginning: [
                {
                    name: "Intercompany Receivables",
                    type: AccountType.otherAssets,
                    subtype: AccountSubtype.intercompanyReceivables,
                },
                {
                    name: "Loans to Employees",
                    type: AccountType.otherAssets,
                    subtype: AccountSubtype.employeeLoans,
                },
                {
                    name: "Loans to Owner",
                    if: isSolePropLedger,
                    type: AccountType.otherAssets,
                    subtype: AccountSubtype.loansToOwner,
                },
                {
                    name: "Loans to Shareholders",
                    if: isCorporationLedger,
                    type: AccountType.otherAssets,
                    subtype: AccountSubtype.loansToShareholders,
                },
                {
                    name: "Loans to Partners",
                    if: isPartnershipLedger,
                    type: AccountType.otherAssets,
                    subtype: AccountSubtype.loansToPartners,
                },
                {
                    name: "Mortgage and Real Estate Loans",
                    type: AccountType.otherAssets,
                    subtype: AccountSubtype.mortgageAndRealEstateLoans,
                },
                {
                    name: "Security Deposits",
                    type: AccountType.otherAssets,
                    subtype: AccountSubtype.securityDeposits,
                },
                {
                    name: "Depletable Assets",
                    type: AccountType.otherAssets,
                    subtype: AccountSubtype.depletableAssets,
                },
                {
                    name: "Accumulated Depletion",
                    type: AccountType.otherAssets,
                    subtype: AccountSubtype.accumulatedDepletion,
                },
            ],
            subtypesStartFromEnd: [
                {
                    name: "Other Long-Term Assets",
                    type: AccountType.otherAssets,
                    subtype: AccountSubtype.otherLongTermAssets,
                },
            ],
        },
        {
            typeKey: AccountType.longTermLoans,
            startFrom: 250000,
            upTo: 259999,
            endFrom: 259900,
            offset: 100,
            subtypesStartFromBeginning: [
                {
                    name: "Long-Term Loans",
                    type: AccountType.longTermLoans,
                    subtype: AccountSubtype.longTermLoans,
                },
                {
                    name: "Loans from Owner",
                    if: isSolePropLedger,
                    type: AccountType.longTermLoans,
                    subtype: AccountSubtype.loansFromOwner,
                },
                {
                    name: "Loans from Shareholders",
                    if: isCorporationLedger,
                    type: AccountType.longTermLoans,
                    subtype: AccountSubtype.loansFromShareholders,
                },
                {
                    name: "Loans from Partners",
                    if: isPartnershipLedger,
                    type: AccountType.longTermLoans,
                    subtype: AccountSubtype.loansFromPartners,
                },
            ],
            subtypesStartFromEnd: [
                {
                    name: "Less Current Maturities of Long-Term Debt",
                    type: AccountType.longTermLoans,
                    subtype: AccountSubtype.lessCurrentMaturitiesOfLongTermDebt,
                },
            ],
        },
    ];

export type AccountSubtypesForFinancialAccountGroups =
    | AccountSubtype.bankAccount
    | AccountSubtype.paymentProcessor
    | AccountSubtype.creditCard
    | AccountSubtype.cashOnHand
    | AccountSubtype.payrollAccount
    | AccountSubtype.otherCurrentLiabilities;

export const defaultCashChartOfAccountsFinancialAccountGroups: Record<
    AccountSubtypesForFinancialAccountGroups,
    FinancialAccountsGroup
> = {
    [AccountSubtype.cashOnHand]: {
        startFrom: 100000,
        upTo: 100099,
        name: () => `Cash account`,
        type: AccountType.cash,
        subtype: AccountSubtype.cashOnHand,
        class: AccountClass.assets,
        clearingRole: AccountRole.cashOnHand,
    },
    [AccountSubtype.bankAccount]: {
        startFrom: 100100,
        upTo: 100199,
        name: (account: FinancialAccountBase) =>
            `Bank Account - ${account.name}${
                account.accountNumberMask
                    ? ` - ${account.accountNumberMask}`
                    : ""
            }`,
        type: AccountType.cash,
        subtype: AccountSubtype.bankAccount,
        class: AccountClass.assets,
        clearingRole: AccountRole.bankTransferClearing,
    },
    [AccountSubtype.paymentProcessor]: {
        startFrom: 100200,
        upTo: 100299,
        name: (account: FinancialAccountBase) =>
            `Payment Processor - ${capitalize(account.sourceType)} - ${account.name}${
                account.accountNumberMask
                    ? ` - ${account.accountNumberMask}`
                    : ""
            }`,
        type: AccountType.cash,
        subtype: AccountSubtype.paymentProcessor,
        class: AccountClass.assets,
        clearingRole: AccountRole.paymentTransferClearing,
    },
    [AccountSubtype.creditCard]: {
        startFrom: 210000,
        upTo: 210099,
        name: (account: FinancialAccountBase) =>
            `Credit Card - ${account.name}${
                account.accountNumberMask
                    ? ` - ${account.accountNumberMask}`
                    : ""
            }`,
        type: AccountType.creditCards,
        subtype: AccountSubtype.creditCard,
        class: AccountClass.liabilities,
        clearingRole: AccountRole.creditCardClearing,
    },
    [AccountSubtype.payrollAccount]: {
        startFrom: 220100,
        upTo: 220199,
        name: (account: FinancialAccountBase) =>
            `${capitalize(account.sourceType)} - ${account.name}`,
        type: AccountType.payrollLiabilities,
        subtype: AccountSubtype.payrollAccount,
        class: AccountClass.liabilities,
        clearingRole: AccountRole.payrollClearing,
    },
    [AccountSubtype.otherCurrentLiabilities]: {
        startFrom: 249901,
        upTo: 249999,
        name: (account: FinancialAccountBase) =>
            `${capitalize(account.sourceType)} - ${account.name}`,
        type: AccountType.otherCurrentLiabilities,
        subtype: AccountSubtype.otherCurrentLiabilities,
        class: AccountClass.liabilities,
        clearingRole: AccountRole.reimbursementsClearing,
    },
};

export const entityTypeToEquityAccounts: Record<
    EntityProfileType,
    ChartOfAccounts<any>
> = {
    [EntityProfileType.C_CORP]: defaultSCorpEquityChartOfAccounts,
    [EntityProfileType.S_CORP]: defaultSCorpEquityChartOfAccounts,
    [EntityProfileType.SOLE_PROPRIETORSHIP]:
        defaultSolePropEquityChartOfAccounts,
    [EntityProfileType.PARTNERSHIP]: defaultPartnershipEquityChartOfAccounts,
    [EntityProfileType.NO_LEGAL_ENTITY]: defaultSolePropEquityChartOfAccounts,
    [EntityProfileType.SMLLC]: defaultSolePropEquityChartOfAccounts,
    [EntityProfileType.MMLLC]: defaultPartnershipEquityChartOfAccounts,
};

export const mappingFinancialTypeToFinancialAccountGroup: Record<
    FinancialAccountType,
    FinancialAccountsGroup
> = {
    [FinancialAccountType.CHECKING]:
        defaultCashChartOfAccountsFinancialAccountGroups[
            AccountSubtype.bankAccount
        ],
    [FinancialAccountType.SAVINGS]:
        defaultCashChartOfAccountsFinancialAccountGroups[
            AccountSubtype.bankAccount
        ],
    [FinancialAccountType.OTHER]:
        defaultCashChartOfAccountsFinancialAccountGroups[
            AccountSubtype.bankAccount
        ],
    [FinancialAccountType.INVESTMENT]:
        defaultCashChartOfAccountsFinancialAccountGroups[
            AccountSubtype.bankAccount
        ],
    [FinancialAccountType.LOAN]:
        defaultCashChartOfAccountsFinancialAccountGroups[
            AccountSubtype.creditCard
        ],
    [FinancialAccountType.LINE_OF_CREDIT]:
        defaultCashChartOfAccountsFinancialAccountGroups[
            AccountSubtype.creditCard
        ],
    [FinancialAccountType.CREDIT_CARD]:
        defaultCashChartOfAccountsFinancialAccountGroups[
            AccountSubtype.creditCard
        ],
    [FinancialAccountType.PAYMENT_PROCESSOR]:
        defaultCashChartOfAccountsFinancialAccountGroups[
            AccountSubtype.paymentProcessor
        ],
    [FinancialAccountType.CASH]:
        defaultCashChartOfAccountsFinancialAccountGroups[
            AccountSubtype.cashOnHand
        ],
};

export const defaultCashChartOfAccounts: ChartOfAccountsTypesWithDynamicNumberingTypeNew =
    [
        // 1) CASH (codes ~100000–109999)
        {
            typeKey: AccountType.cash,
            startFrom: 100000,
            upTo: 109999,
            endFrom: 109900,
            offset: 100,
            accountClass: accountTypeToAccountClass[AccountType.cash],
            subtypesStartFromBeginning: [
                {
                    key: AccountSubtype.cashOnHand,
                    accounts: [
                        {
                            name: "Cash on Hand",
                        },
                    ],
                },
                {
                    key: AccountSubtype.bankAccount,
                    accountsAreAutomaticallyGenerated: true,
                },
                {
                    key: AccountSubtype.paymentProcessor,
                    accountsAreAutomaticallyGenerated: true,
                },
            ],
            subtypesStartFromEnd: [
                {
                    key: AccountSubtype.transferClearing,
                    accounts: [
                        {
                            name: "Bank Transfer Clearing",
                        },
                        {
                            name: "Payment Transfer Clearing",
                        },
                    ],
                },
            ],
        },

        // 2) ACCOUNTS RECEIVABLE (110000–119999)
        {
            typeKey: AccountType.accountsReceivable,
            startFrom: 110000,
            upTo: 119999,
            endFrom: 119900,
            offset: 100,
            accountClass:
                accountTypeToAccountClass[AccountType.accountsReceivable],
            subtypesStartFromBeginning: [
                {
                    key: AccountSubtype.accountsReceivable,
                    accounts: [
                        {
                            name: "Accounts Receivable",
                        },
                    ],
                },
            ],
            subtypesStartFromEnd: [
                {
                    key: AccountSubtype.allowanceForBadDebts,
                    accounts: [
                        {
                            name: "Allowance for Bad Debts",
                        },
                    ],
                },
            ],
        },

        // 3) INVENTORY (120000–129999)
        {
            typeKey: AccountType.inventory,
            startFrom: 120000,
            upTo: 129999,
            endFrom: 129900,
            offset: 100,
            accountClass: accountTypeToAccountClass[AccountType.inventory],
            subtypesStartFromBeginning: [
                {
                    key: AccountSubtype.inventory,
                    accounts: [
                        {
                            name: "Inventory",
                        },
                    ],
                },
            ],
            subtypesStartFromEnd: [],
        },

        // 4) OTHER CURRENT ASSETS (130000–139999)
        {
            typeKey: AccountType.otherCurrentAssets,
            startFrom: 130000,
            upTo: 139999,
            endFrom: 139900,
            offset: 100,
            accountClass:
                accountTypeToAccountClass[AccountType.otherCurrentAssets],
            subtypesStartFromBeginning: [
                {
                    key: AccountSubtype.prepaidExpenses,
                    accounts: [
                        {
                            name: "Prepaid Expenses",
                        },
                    ],
                },
                {
                    key: AccountSubtype.accruedRevenue,
                    accounts: [
                        {
                            name: "Accrued Revenue",
                        },
                    ],
                },
            ],
            subtypesStartFromEnd: [
                {
                    key: AccountSubtype.otherCurrentAssets,
                    accounts: [
                        {
                            name: "Other Current Assets",
                        },
                    ],
                },
            ],
        },

        // 5) INVESTMENTS (140000–149999)
        {
            typeKey: AccountType.investments,
            startFrom: 140000,
            upTo: 149999,
            endFrom: 149900,
            offset: 100,
            accountClass: accountTypeToAccountClass[AccountType.investments],
            subtypesStartFromBeginning: [
                {
                    key: AccountSubtype.usGovernmentObligations,
                    accounts: [
                        {
                            name: "U.S. Government Obligations",
                        },
                    ],
                },
                {
                    key: AccountSubtype.taxExemptSecurities,
                    accounts: [
                        {
                            name: "Tax-Exempt Securities",
                        },
                    ],
                },
                {
                    key: AccountSubtype.investments,
                    accounts: [
                        {
                            name: "Investments",
                        },
                    ],
                },
            ],
            subtypesStartFromEnd: [],
        },

        // 6) FIXED ASSETS (150000–159999)
        {
            typeKey: AccountType.fixedAssets,
            startFrom: 150000,
            upTo: 159999,
            endFrom: 159900,
            offset: 100,
            accountClass: accountTypeToAccountClass[AccountType.fixedAssets],
            subtypesStartFromBeginning: [
                {
                    key: AccountSubtype.land,
                    accounts: [
                        {
                            name: "Land",
                        },
                    ],
                },
                {
                    key: AccountSubtype.buildings,
                    accounts: [
                        {
                            name: "Buildings",
                        },
                    ],
                },
                {
                    key: AccountSubtype.leaseholds,
                    accounts: [
                        {
                            name: "Leasehold Improvements",
                        },
                    ],
                },
                {
                    key: AccountSubtype.vehicles,
                    accounts: [
                        {
                            name: "Vehicles",
                        },
                    ],
                },
                {
                    key: AccountSubtype.equipment,
                    accounts: [
                        {
                            name: "Equipment",
                        },
                    ],
                },
                {
                    key: AccountSubtype.furnitureAndFixtures,
                    accounts: [
                        {
                            name: "Furniture and Fixtures",
                        },
                    ],
                },
            ],
            subtypesStartFromEnd: [
                {
                    key: AccountSubtype.accumulatedDepreciation,
                    accounts: [
                        {
                            name: "Accumulated Depreciation",
                        },
                    ],
                },
                {
                    key: AccountSubtype.otherFixedAssets,
                    accounts: [
                        {
                            name: "Other Fixed Assets",
                        },
                    ],
                },
            ],
        },

        // 7) INTANGIBLE ASSETS (160000–169999)
        {
            typeKey: AccountType.intangibleAssets,
            startFrom: 160000,
            upTo: 169999,
            endFrom: 169900,
            offset: 100,
            accountClass:
                accountTypeToAccountClass[AccountType.intangibleAssets],
            subtypesStartFromBeginning: [
                {
                    key: AccountSubtype.startupCosts,
                    accounts: [
                        {
                            name: "Startup Costs",
                        },
                    ],
                },
                {
                    key: AccountSubtype.trademarksAndPatents,
                    accounts: [
                        {
                            name: "Trademarks and Patents",
                        },
                    ],
                },
                {
                    key: AccountSubtype.goodwill,
                    accounts: [
                        {
                            name: "Goodwill",
                        },
                    ],
                },
            ],
            subtypesStartFromEnd: [
                {
                    key: AccountSubtype.accumulatedAmortization,
                    accounts: [
                        {
                            name: "Accumulated Amortization",
                        },
                    ],
                },
                {
                    key: AccountSubtype.otherIntangibleAssets,
                    accounts: [
                        {
                            name: "Other Intangible Assets",
                        },
                    ],
                },
            ],
        },

        // OTHER ASSETS (190000–199999)
        {
            typeKey: AccountType.otherAssets,
            startFrom: 190000,
            upTo: 199999,
            endFrom: 199900,
            offset: 100,
            accountClass: accountTypeToAccountClass[AccountType.otherAssets],
            subtypesStartFromBeginning: [
                {
                    key: AccountSubtype.intercompanyReceivables,
                    accounts: [
                        {
                            name: "Intercompany Receivables",
                        },
                    ],
                },
                {
                    key: AccountSubtype.employeeLoans,
                    accounts: [
                        {
                            name: "Loans to Employees",
                        },
                    ],
                },
                {
                    if: isSolePropLedger,
                    key: AccountSubtype.loansToOwner,
                    accounts: [
                        {
                            name: "Loans to Owner",
                        },
                    ],
                },
                {
                    if: isCorporationLedger,

                    key: AccountSubtype.loansToShareholders,
                    accounts: [
                        {
                            name: "Loans to Shareholders",
                        },
                    ],
                },
                {
                    if: isPartnershipLedger,
                    key: AccountSubtype.loansToPartners,
                    accounts: [
                        {
                            name: "Loans to Partners",
                        },
                    ],
                },
                {
                    key: AccountSubtype.mortgageAndRealEstateLoans,
                    accounts: [
                        {
                            name: "Mortgage and Real Estate Loans",
                        },
                    ],
                },
                {
                    key: AccountSubtype.securityDeposits,
                    accounts: [
                        {
                            name: "Security Deposits",
                        },
                    ],
                },
                {
                    key: AccountSubtype.depletableAssets,
                    accounts: [
                        {
                            name: "Depletable Assets",
                        },
                    ],
                },
                {
                    key: AccountSubtype.accumulatedDepletion,
                    accounts: [
                        {
                            name: "Accumulated Depletion",
                        },
                    ],
                },
            ],
            subtypesStartFromEnd: [
                {
                    key: AccountSubtype.otherLongTermAssets,
                    accounts: [
                        {
                            name: "Other Long-Term Assets",
                        },
                    ],
                },
            ],
        },

        // 8) ACCOUNTS PAYABLE (200000–209999)
        {
            typeKey: AccountType.accountsPayable,
            startFrom: 200000,
            upTo: 209999,
            endFrom: 209900,
            offset: 100,
            accountClass:
                accountTypeToAccountClass[AccountType.accountsPayable],
            subtypesStartFromBeginning: [
                {
                    key: AccountSubtype.accountsPayable,
                    accounts: [
                        {
                            name: "Accounts Payable",
                        },
                    ],
                },
            ],
            subtypesStartFromEnd: [],
        },

        // 9) CREDIT CARDS (210000–219999)
        {
            typeKey: AccountType.creditCards,
            startFrom: 210000,
            upTo: 219999,
            endFrom: 219900,
            offset: 100,
            accountClass: accountTypeToAccountClass[AccountType.creditCards],
            subtypesStartFromBeginning: [
                {
                    key: AccountSubtype.creditCard,
                    accountsAreAutomaticallyGenerated: true,
                },
            ],
            subtypesStartFromEnd: [
                {
                    key: AccountSubtype.creditCardClearing,
                    accounts: [
                        {
                            name: "Credit Card Clearing",
                        },
                    ],
                },
            ],
        },

        // 10) PAYROLL LIABILITIES (220000–229999)
        {
            typeKey: AccountType.payrollLiabilities,
            startFrom: 220000,
            upTo: 229999,
            endFrom: 229900,
            offset: 100,
            accountClass:
                accountTypeToAccountClass[AccountType.payrollLiabilities],
            subtypesStartFromBeginning: [
                {
                    key: AccountSubtype.payrollPayable,
                    accounts: [
                        {
                            name: "Payroll Payable",
                        },
                    ],
                },
            ],
            subtypesStartFromEnd: [],
        },

        // 11) SHORT-TERM LOANS (230000–239999)
        {
            typeKey: AccountType.shortTermLoans,
            startFrom: 230000,
            upTo: 239999,
            endFrom: 239900,
            offset: 100,
            accountClass: accountTypeToAccountClass[AccountType.shortTermLoans],
            subtypesStartFromBeginning: [
                {
                    key: AccountSubtype.lineOfCredit,
                    accounts: [
                        {
                            name: "Line of Credit",
                        },
                    ],
                },
                {
                    key: AccountSubtype.shortTermLoans,
                    accounts: [
                        {
                            name: "Short-Term Loans",
                        },
                    ],
                },
            ],
            subtypesStartFromEnd: [
                {
                    key: AccountSubtype.currentMaturitiesOfLongTermDebt,
                    accounts: [
                        {
                            name: "Current Maturities of Long-Term Debt",
                        },
                    ],
                },
            ],
        },

        // 12) OTHER CURRENT LIABILITIES (240000–249999)
        {
            typeKey: AccountType.otherCurrentLiabilities,
            startFrom: 240000,
            upTo: 249999,
            endFrom: 249900,
            offset: 100,
            accountClass:
                accountTypeToAccountClass[AccountType.otherCurrentLiabilities],
            subtypesStartFromBeginning: [
                {
                    key: AccountSubtype.salesTaxPayable,
                    accounts: [
                        {
                            name: "Sales Tax Payable",
                        },
                    ],
                },
                {
                    key: AccountSubtype.deferredRevenue,
                    accounts: [
                        {
                            name: "Deferred Revenue",
                        },
                    ],
                },
                {
                    key: AccountSubtype.accruedExpenses,
                    accounts: [
                        {
                            name: "Accrued Expenses",
                        },
                    ],
                },
                {
                    key: AccountSubtype.customerDeposits,
                    accounts: [
                        {
                            name: "Customer Deposits",
                        },
                    ],
                },
            ],
            subtypesStartFromEnd: [
                {
                    key: AccountSubtype.otherCurrentLiabilities,
                    accounts: [
                        {
                            name: "Other Current Liabilities",
                        },
                    ],
                },
            ],
        },

        // LONG-TERM LOANS (250000–259999)
        {
            typeKey: AccountType.longTermLoans,
            startFrom: 250000,
            upTo: 259999,
            endFrom: 259900,
            offset: 100,
            accountClass: accountTypeToAccountClass[AccountType.longTermLoans],
            subtypesStartFromBeginning: [
                {
                    key: AccountSubtype.longTermLoans,
                    accounts: [
                        {
                            name: "Long-Term Loans",
                        },
                    ],
                },
                {
                    if: isSolePropLedger,
                    key: AccountSubtype.loansFromOwner,
                    accounts: [
                        {
                            name: "Loans from Owner",
                        },
                    ],
                },
                {
                    if: isCorporationLedger,
                    key: AccountSubtype.loansFromShareholders,
                    accounts: [
                        {
                            name: "Loans from Shareholders",
                        },
                    ],
                },
                {
                    if: isPartnershipLedger,
                    key: AccountSubtype.loansFromPartners,
                    accounts: [
                        {
                            name: "Loans from Partners",
                        },
                    ],
                },
            ],
            subtypesStartFromEnd: [
                {
                    key: AccountSubtype.lessCurrentMaturitiesOfLongTermDebt,
                    accounts: [
                        {
                            name: "Less Current Maturities of Long-Term Debt",
                        },
                    ],
                },
            ],
        },

        // 13) OTHER LIABILITIES (290000–299999)
        {
            typeKey: AccountType.otherLiabilities,
            startFrom: 290000,
            upTo: 299999,
            endFrom: 299900,
            offset: 100,
            accountClass:
                accountTypeToAccountClass[AccountType.otherLiabilities],
            subtypesStartFromBeginning: [
                {
                    key: AccountSubtype.intercompanyPayables,
                    accounts: [
                        {
                            name: "Intercompany Payables",
                        },
                    ],
                },
            ],
            subtypesStartFromEnd: [
                {
                    key: AccountSubtype.otherLiabilities,
                    accounts: [
                        {
                            name: "Other Long-Term Liabilities",
                        },
                    ],
                },
            ],
        },

        // EQUITY (300000–399999)
        {
            typeKey: AccountType.equity,
            startFrom: 300000,
            upTo: 399999,
            endFrom: 399000,
            offset: 1000,
            accountClass: accountTypeToAccountClass[AccountType.equity],
            subtypesStartFromBeginning: [
                {
                    if: isSolePropLedger,
                    key: AccountSubtype.contributions,
                    accounts: [
                        {
                            name: "Owner Investments",
                        },
                    ],
                },
                {
                    if: isSolePropLedger,
                    key: AccountSubtype.distributions,
                    accounts: [
                        {
                            name: "Owner Draws",
                        },
                    ],
                },
                {
                    if: isSolePropLedger,
                    key: AccountSubtype.capital,
                    accounts: [
                        {
                            name: "Owner Capital",
                        },
                    ],
                },
                {
                    if: isPartnershipLedger,
                    key: AccountSubtype.contributions,
                    accounts: [
                        {
                            name: "Contributions",
                        },
                    ],
                },
                {
                    if: isPartnershipLedger,
                    key: AccountSubtype.distributions,
                    accounts: [
                        {
                            name: "Distributions",
                        },
                    ],
                },
                {
                    if: isPartnershipLedger,
                    key: AccountSubtype.capital,
                    accounts: [
                        {
                            name: "Capital",
                        },
                    ],
                },
                {
                    if: isCorporationLedger,
                    key: AccountSubtype.commonStock,
                    accounts: [
                        {
                            name: "Common Stock",
                        },
                    ],
                },
                {
                    if: isCorporationLedger,
                    key: AccountSubtype.additionalPaidInCapital,
                    accounts: [
                        {
                            name: "Additional Paid-In Capital",
                        },
                    ],
                },
                {
                    if: isCorporationLedger,
                    key: AccountSubtype.contributions,
                    accounts: [
                        {
                            name: "Contributions",
                        },
                    ],
                },
                {
                    if: isCorporationLedger,
                    key: AccountSubtype.distributions,
                    accounts: [
                        {
                            name: "Distributions",
                        },
                    ],
                },
                {
                    if: isCorporationLedger,
                    key: AccountSubtype.dividends,
                    accounts: [
                        {
                            name: "Dividends",
                        },
                    ],
                },
                {
                    if: isCorporationLedger,
                    key: AccountSubtype.retainedEarnings,
                    accounts: [
                        {
                            name: "Retained Earnings",
                        },
                    ],
                },
                {
                    if: isCorporationLedger,
                    key: AccountSubtype.adjustmentsToShareholdersEquity,
                    accounts: [
                        {
                            name: "Adjustments to Shareholders' Equity",
                        },
                    ],
                },
                {
                    if: isCorporationLedger,
                    key: AccountSubtype.costOfTreasuryStock,
                    accounts: [
                        {
                            name: "Cost of Treasury Stock",
                        },
                    ],
                },
            ],
            subtypesStartFromEnd: [],
        },

        // 14) INCOME (400000–499999)
        {
            typeKey: AccountType.income,
            startFrom: 400000,
            upTo: 499999,
            endFrom: 499099,
            offset: 1000,
            accountClass: accountTypeToAccountClass[AccountType.income],
            subtypesStartFromBeginning: [
                {
                    key: AccountSubtype.revenue,
                    accounts: [
                        {
                            name: "Revenue",
                        },
                    ],
                },
                {
                    key: AccountSubtype.refundsAndReturns,
                    accounts: [
                        {
                            name: "Refunds and Returns",
                        },
                    ],
                },
                {
                    key: AccountSubtype.discountsAndAllowances,
                    accounts: [
                        {
                            name: "Discounts and Allowances",
                        },
                    ],
                },
            ],
            subtypesStartFromEnd: [
                {
                    key: AccountSubtype.uncategorizedIncome,
                    accounts: [
                        {
                            name: "Uncategorized Income",
                        },
                    ],
                },
            ],
        },

        // 15) COGS (500000–509999)
        {
            typeKey: AccountType.cogs,
            startFrom: 500000,
            upTo: 509999,
            endFrom: 509900,
            offset: 100,
            accountClass: accountTypeToAccountClass[AccountType.cogs],
            subtypesStartFromBeginning: [
                {
                    key: AccountSubtype.cogsGoodsAndMaterials,
                    accounts: [
                        {
                            name: "COGS - Goods and Materials",
                        },
                    ],
                },
                {
                    key: AccountSubtype.cogsLabor,
                    accounts: [
                        {
                            name: "COGS - Labor",
                        },
                    ],
                },
                {
                    key: AccountSubtype.cogsOther,
                    accounts: [
                        {
                            name: "COGS - Other",
                        },
                    ],
                },
            ],
            subtypesStartFromEnd: [],
        },

        // PAYROLL EXPENSES (600000–609999) — absorbed into Operating Expenses
        {
            typeKey: AccountType.operatingExpenses,
            startFrom: 600000,
            upTo: 609999,
            endFrom: 609900,
            offset: 100,
            accountClass:
                accountTypeToAccountClass[AccountType.operatingExpenses],
            subtypesStartFromBeginning: [
                {
                    key: AccountSubtype.salariesAndWages,
                    accounts: [
                        {
                            name: "Salaries and Wages",
                        },
                    ],
                },
                {
                    key: AccountSubtype.contractLabor,
                    accounts: [
                        {
                            name: "Contract Labor",
                        },
                    ],
                },
                {
                    if: isPartnershipLedger,
                    key: AccountSubtype.guaranteedPaymentsToPartners,
                    accounts: [
                        {
                            name: "Guaranteed Payments to Partners",
                        },
                    ],
                },
                {
                    if: isCorporationLedger,
                    key: AccountSubtype.officersCompensation,
                    accounts: [
                        {
                            name: "Officer's Compensation",
                        },
                    ],
                },
                {
                    key: AccountSubtype.commissions,
                    accounts: [
                        {
                            name: "Commissions",
                        },
                    ],
                },
            ],
            subtypesStartFromEnd: [
                {
                    key: AccountSubtype.salariesAndWagesClearing,
                    accounts: [
                        {
                            name: "Salaries and Wages - Clearing",
                        },
                    ],
                },
            ],
        },

        // 16) PAYROLL TAXES (610000–619999) — absorbed into Operating Expenses
        {
            typeKey: AccountType.operatingExpenses,
            startFrom: 610000,
            upTo: 619999,
            endFrom: 619900,
            offset: 100,
            accountClass:
                accountTypeToAccountClass[AccountType.operatingExpenses],
            subtypesStartFromBeginning: [
                {
                    key: AccountSubtype.payrollTaxes,
                    accounts: [
                        {
                            name: "Payroll Taxes",
                        },
                    ],
                },
            ],
            subtypesStartFromEnd: [],
        },

        // 17) EMPLOYEE BENEFITS (620000–629999) — absorbed into Operating Expenses
        {
            typeKey: AccountType.operatingExpenses,
            startFrom: 620000,
            upTo: 629999,
            endFrom: 629900,
            offset: 100,
            accountClass:
                accountTypeToAccountClass[AccountType.operatingExpenses],
            subtypesStartFromBeginning: [
                {
                    key: AccountSubtype.employeeBenefits,
                    accounts: [
                        {
                            name: "Employee Benefits",
                        },
                    ],
                },
                {
                    key: AccountSubtype.retirementBenefits,
                    accounts: [
                        {
                            name: "Retirement Benefits",
                        },
                    ],
                },
            ],
            subtypesStartFromEnd: [],
        },

        // 18) OPERATING EXPENSES (700000–799999)
        {
            typeKey: AccountType.operatingExpenses,
            startFrom: 700000,
            upTo: 799999,
            endFrom: 799099,
            offset: 1000,
            offsetEndFrom: (index) => (index === 0 ? 99 : 1000),
            accountClass:
                accountTypeToAccountClass[AccountType.operatingExpenses],
            subtypesStartFromBeginning: [
                {
                    key: AccountSubtype.advertisingAndMarketing,
                    accounts: [
                        {
                            name: "Advertising and Marketing",
                        },
                    ],
                },
                {
                    key: AccountSubtype.learningAndDevelopment,
                    accounts: [
                        {
                            name: "Learning and Development",
                        },
                    ],
                },
                {
                    key: AccountSubtype.licensesAndPermits,
                    accounts: [
                        {
                            name: "Licenses and Permits",
                        },
                    ],
                },
                {
                    key: AccountSubtype.propertyTaxes,
                    accounts: [
                        {
                            name: "Property Taxes",
                        },
                    ],
                },
                {
                    key: AccountSubtype.entertainment,
                    accounts: [
                        {
                            name: "Entertainment",
                        },
                    ],
                },
                {
                    key: AccountSubtype.insurance,
                    accounts: [
                        {
                            name: "Insurance - Business",
                        },
                        {
                            name: (entityType: EntityProfileType) => {
                                let name = "Insurance - ";
                                if (isSolePropLedger(entityType)) {
                                    name += "Owner";
                                }
                                if (isCorporationLedger(entityType)) {
                                    name += "Officers";
                                }
                                if (isPartnershipLedger(entityType)) {
                                    name += "Partners";
                                }
                                return name;
                            },
                        },
                    ],
                },
                {
                    key: AccountSubtype.internetAndPhone,
                    accounts: [
                        {
                            name: "Internet and Phone",
                        },
                    ],
                },
                {
                    key: AccountSubtype.facilities,
                    accounts: [
                        {
                            name: "Facilities - Repairs and Maintenance",
                        },
                        {
                            name: "Facilities - Cleaning Fees",
                        },
                        {
                            name: "Facilities - Management Fees",
                        },
                    ],
                },
                {
                    key: AccountSubtype.furnitureAndFixtures,
                    accounts: [
                        {
                            name: "Furniture and Fixtures",
                        },
                    ],
                },
                {
                    key: AccountSubtype.meals,
                    accounts: [
                        {
                            name: "Meals",
                        },
                        {
                            name: "Meals - Employee Catering",
                        },
                    ],
                },
                {
                    key: AccountSubtype.merchantAndBankFees,
                    accounts: [
                        {
                            name: "Merchant and Bank Fees",
                        },
                    ],
                },
                {
                    key: AccountSubtype.suppliesAndEquipment,
                    accounts: [
                        {
                            name: "Supplies and Equipment",
                        },
                    ],
                },
                {
                    key: AccountSubtype.softwareAndSubscriptions,
                    accounts: [
                        {
                            name: "Software and Subscriptions",
                        },
                    ],
                },
                {
                    key: AccountSubtype.officeExpenses,
                    accounts: [
                        {
                            name: "Office Expenses",
                        },
                    ],
                },
                {
                    key: AccountSubtype.officeUtilities,
                    accounts: [
                        {
                            name: "Office Utilities",
                        },
                    ],
                },
                {
                    key: AccountSubtype.legalAndProfessionalFees,
                    accounts: [
                        {
                            name: "Legal and Professional Fees",
                        },
                    ],
                },
                {
                    key: AccountSubtype.rentAndLease,
                    accounts: [
                        {
                            name: "Rent and Lease",
                        },
                    ],
                },
                {
                    key: AccountSubtype.travel,
                    accounts: [
                        {
                            name: "Travel",
                        },
                    ],
                },
                {
                    key: AccountSubtype.vehicle,
                    accounts: [
                        {
                            name: "Vehicle",
                        },
                        {
                            name: "Vehicle - Leases",
                        },
                        {
                            name: "Vehicle - Parking and Tolls",
                        },
                    ],
                },
                {
                    key: AccountSubtype.loanFees,
                    accounts: [
                        {
                            name: "Loan Fees",
                        },
                    ],
                },
            ],
            subtypesStartFromEnd: [
                {
                    key: AccountSubtype.uncategorizedExpenses,
                    accounts: [
                        {
                            name: "Uncategorized Expenses",
                        },
                    ],
                },
                {
                    key: AccountSubtype.otherOperatingExpenses,
                    accounts: [
                        {
                            name: "Other Operating Expenses",
                        },
                    ],
                },
            ],
        },

        // 19) OTHER INCOME (800000–849999)
        {
            typeKey: AccountType.otherIncome,
            startFrom: 800000,
            upTo: 849999,
            endFrom: 849000,
            accountClass: accountTypeToAccountClass[AccountType.otherIncome],
            offset: 1000,
            subtypesStartFromBeginning: [
                {
                    key: AccountSubtype.rentalIncome,
                    accounts: [
                        {
                            name: "Rental Income",
                        },
                    ],
                },
                {
                    key: AccountSubtype.interestIncome,
                    accounts: [
                        {
                            name: "Interest Income",
                        },
                    ],
                },
                {
                    key: AccountSubtype.dividendIncome,
                    accounts: [
                        {
                            name: "Dividend Income",
                        },
                    ],
                },
                {
                    key: AccountSubtype.rebateIncome,
                    accounts: [
                        {
                            name: "Rebate Income",
                        },
                    ],
                },
                {
                    key: AccountSubtype.royaltyIncome,
                    accounts: [
                        {
                            name: "Royalty Income",
                        },
                    ],
                },
            ],
            subtypesStartFromEnd: [
                {
                    key: AccountSubtype.otherIncome,
                    accounts: [
                        {
                            name: "Other Income",
                        },
                    ],
                },
                {
                    key: AccountSubtype.gainLossAssetDisposals,
                    accounts: [
                        {
                            name: "Gain (Loss) Asset Disposals",
                        },
                    ],
                },
            ],
        },

        // 20) OTHER EXPENSES (850000–899999)
        {
            typeKey: AccountType.otherExpenses,
            startFrom: 850000,
            upTo: 899999,
            endFrom: 899000,
            accountClass: accountTypeToAccountClass[AccountType.otherExpenses],
            offset: 1000,
            subtypesStartFromBeginning: [
                {
                    key: AccountSubtype.interestExpense,
                    accounts: [
                        {
                            name: "Interest Expense",
                        },
                    ],
                },
                {
                    key: AccountSubtype.depreciation,
                    accounts: [
                        {
                            name: "Depreciation",
                        },
                    ],
                },
                {
                    key: AccountSubtype.amortization,
                    accounts: [
                        {
                            name: "Amortization",
                        },
                    ],
                },
                {
                    key: AccountSubtype.depletion,
                    accounts: [
                        {
                            name: "Depletion",
                        },
                    ],
                },
                {
                    key: AccountSubtype.finesAndPenalties,
                    accounts: [
                        {
                            name: "Fines and Penalties",
                        },
                    ],
                },
                {
                    key: AccountSubtype.badDebts,
                    accounts: [
                        {
                            name: "Bad Debts",
                        },
                    ],
                },
                {
                    key: AccountSubtype.donations,
                    accounts: [
                        {
                            name: "Donations",
                        },
                    ],
                },
            ],
            subtypesStartFromEnd: [
                {
                    key: AccountSubtype.otherExpense,
                    accounts: [
                        {
                            name: "Other Expense",
                        },
                    ],
                },
            ],
        },

        // 21) TAX EXPENSES (900000–909999) — absorbed into Other Expenses
        {
            typeKey: AccountType.otherExpenses,
            startFrom: 900000,
            upTo: 909999,
            endFrom: 909900,
            accountClass: accountTypeToAccountClass[AccountType.otherExpenses],
            offset: 100,
            subtypesStartFromBeginning: [
                {
                    key: AccountSubtype.federalTaxes,
                    accounts: [
                        {
                            name: "Federal Taxes",
                        },
                    ],
                },
                {
                    key: AccountSubtype.stateAndLocalTaxes,
                    accounts: [
                        {
                            name: "State and Local Taxes",
                        },
                    ],
                },
            ],
            subtypesStartFromEnd: [],
        },
    ];

interface SubtypeBorders {
    startFrom: number;
    upTo: number;
}

type SubtypeBordersForEntityType = Map<
    AccountType,
    {
        startFrom: number;
        upTo: number;
        subtypeBorders: Map<AccountSubtype, SubtypeBorders>;
    }
>;

function generateSubtypeBordersForEntityType(
    entityType: EntityProfileType,
    scheme: ChartOfAccountsTypesWithDynamicNumberingTypeNew,
) {
    return scheme.reduce((bordersMap, type) => {
        const accountSubtypeMap = new Map<
            AccountSubtype,
            { startFrom: number; upTo: number }
        >();
        let currentStartFrom = type.startFrom;

        // Iterate this block's own subtypes (not the union of all blocks
        // sharing the same typeKey) so each block keeps its own startFrom /
        // offset range — getSubtypesInsideType returns the union, which would
        // mis-allocate code ranges here.
        const subtypesStartFromBeginning =
            type.subtypesStartFromBeginning.filter(
                (s) => !s.if || s.if(entityType),
            );
        const subtypesStartFromEnd = type.subtypesStartFromEnd.filter(
            (s) => !s.if || s.if(entityType),
        );

        subtypesStartFromBeginning.forEach((subtype) => {
            accountSubtypeMap.set(subtype.key, {
                startFrom: currentStartFrom,
                upTo: currentStartFrom + type.offset - 1,
            });
            currentStartFrom += type.offset;
        });
        currentStartFrom = type.endFrom;
        let previousStartFrom = type.upTo;
        subtypesStartFromEnd.forEach((subtype, index) => {
            accountSubtypeMap.set(subtype.key, {
                startFrom: currentStartFrom,
                upTo: previousStartFrom,
            });

            previousStartFrom = currentStartFrom - 1;
            currentStartFrom =
                currentStartFrom -
                (type.offsetEndFrom ? type.offsetEndFrom(index) : type.offset);
        });

        // Merge into the existing entry rather than last-write-wins so
        // multiple blocks sharing a typeKey accumulate every subtype range
        // and the entry-level startFrom/upTo span the union of blocks.
        const existing = bordersMap.get(type.typeKey);
        const mergedSubtypeBorders = existing
            ? new Map([...existing.subtypeBorders, ...accountSubtypeMap])
            : accountSubtypeMap;
        bordersMap.set(type.typeKey, {
            subtypeBorders: mergedSubtypeBorders,
            startFrom: existing
                ? Math.min(existing.startFrom, type.startFrom)
                : type.startFrom,
            upTo: existing ? Math.max(existing.upTo, type.upTo) : type.upTo,
        });
        return bordersMap;
    }, new Map() as SubtypeBordersForEntityType);
}

const subtypeBordersForSCorp = generateSubtypeBordersForEntityType(
    EntityProfileType.S_CORP,
    defaultCashChartOfAccounts,
);

const subtypeBordersForSoleProp = generateSubtypeBordersForEntityType(
    EntityProfileType.SOLE_PROPRIETORSHIP,
    defaultCashChartOfAccounts,
);

const subtypeBordersForPartnership = generateSubtypeBordersForEntityType(
    EntityProfileType.PARTNERSHIP,
    defaultCashChartOfAccounts,
);

function lookupBordersForEntityType(
    entityType: EntityProfileType,
    accountType: AccountType,
    accountSubtype: AccountSubtype,
): SubtypeBorders | undefined {
    if (isSolePropLedger(entityType)) {
        return subtypeBordersForSoleProp
            .get(accountType)
            ?.subtypeBorders.get(accountSubtype);
    }
    if (isCorporationLedger(entityType)) {
        return subtypeBordersForSCorp
            .get(accountType)
            ?.subtypeBorders.get(accountSubtype);
    }
    if (isPartnershipLedger(entityType)) {
        return subtypeBordersForPartnership
            .get(accountType)
            ?.subtypeBorders.get(accountSubtype);
    }
    return undefined;
}

export function getSubtypeBordersInCoA(
    entityType: EntityProfileType,
    accountType: AccountType,
    accountSubtype: AccountSubtype,
) {
    return lookupBordersForEntityType(entityType, accountType, accountSubtype);
}

export const getSubtypeBordersInCoAOrThrow = (
    entityType: EntityProfileType,
    accountType: AccountType,
    accountSubtype: AccountSubtype,
) => {
    const result = getSubtypeBordersInCoA(
        entityType,
        accountType,
        accountSubtype,
    );

    if (!result) {
        throw new Error(
            `Subtype borders not found for entity type ${entityType} and type ${accountType} and subtype ${accountSubtype}`,
        );
    }
    return result;
};

function lookupTypeBordersForEntityType(
    entityType: EntityProfileType,
    accountType: AccountType,
): { startFrom: number; upTo: number } | undefined {
    let bordersForEntityType: SubtypeBordersForEntityType | undefined;
    if (isSolePropLedger(entityType)) {
        bordersForEntityType = subtypeBordersForSoleProp;
    } else if (isCorporationLedger(entityType)) {
        bordersForEntityType = subtypeBordersForSCorp;
    } else if (isPartnershipLedger(entityType)) {
        bordersForEntityType = subtypeBordersForPartnership;
    }
    const typeEntry = bordersForEntityType?.get(accountType);
    if (!typeEntry) {
        return undefined;
    }
    return { startFrom: typeEntry.startFrom, upTo: typeEntry.upTo };
}

export function getTypeBordersInCoA(
    entityType: EntityProfileType,
    accountType: AccountType,
) {
    return lookupTypeBordersForEntityType(entityType, accountType);
}

export const getTypeBordersInCoAOrThrow = (
    entityType: EntityProfileType,
    accountType: AccountType,
) => {
    const result = getTypeBordersInCoA(entityType, accountType);

    if (!result) {
        throw new Error(
            `Type borders not found for entity type ${entityType} and type ${accountType}`,
        );
    }
    return result;
};

export function getSubtypesInsideType(
    entityType: EntityProfileType,
    accountType: AccountType,
) {
    // Multiple seed blocks can share the same typeKey (operatingExpenses
    // owns four blocks after the payroll absorption). Union their subtypes
    // so type-picker UIs and auto-fill paths see every absorbed subtype, not
    // just the first matching block's.
    const matchingTypeDeclarations = defaultCashChartOfAccounts.filter(
        (t) => t.typeKey === accountType,
    );
    if (matchingTypeDeclarations.length === 0) {
        throw new Error(`Type ${accountType} not found`);
    }
    return {
        subtypesStartFromBeginning: matchingTypeDeclarations.flatMap((block) =>
            block.subtypesStartFromBeginning.filter(
                (s) => !s.if || s.if(entityType),
            ),
        ),
        subtypesStartFromEnd: matchingTypeDeclarations.flatMap((block) =>
            block.subtypesStartFromEnd.filter((s) => !s.if || s.if(entityType)),
        ),
    };
}

export interface TypeCodeBlock {
    startFrom: number;
    upTo: number;
}

export function getTypeCodeBlocks(accountType: AccountType): TypeCodeBlock[] {
    // operatingExpenses owns non-contiguous seed blocks (600000–629999 payroll + 700000–799999);
    // returning only declared blocks keeps the 630000–699999 inter-block gap out of the allocatable set.
    return defaultCashChartOfAccounts
        .filter((type) => type.typeKey === accountType)
        .map((type) => ({ startFrom: type.startFrom, upTo: type.upTo }))
        .sort((blockA, blockB) => blockA.startFrom - blockB.startFrom);
}

export type ChartOfAccountsItemNameStringOrFunction =
    | string
    | ((entityType: EntityProfileType) => string);

export interface ChartOfAccountsItem {
    name: ChartOfAccountsItemNameStringOrFunction;
    type: AccountType;
    subtype: AccountSubtype;
}

export type ChartOfAccounts<T extends number> = Record<T, ChartOfAccountsItem>;

export interface ChartOfAccountsItemOptional extends ChartOfAccountsItem {
    if?: (entityType: EntityProfileType) => boolean;
}

export interface ChartOfAccountsTypeWithDynamicNumbering {
    typeKey: AccountType;
    startFrom: number;
    upTo: number;
    endFrom: number;
    offset: number;
    subtypesStartFromBeginning: ChartOfAccountsItemOptional[];
    subtypesStartFromEnd: ChartOfAccountsItemOptional[];
}

export type ChartOfAccountsTypesWithDynamicNumberingType =
    ChartOfAccountsTypeWithDynamicNumbering[];

export interface ChartOfAccountsItemNew {
    name: ChartOfAccountsItemNameStringOrFunction;
}

export interface ChartOfAccountsItemOptionalNew extends ChartOfAccountsItemNew {
    if?: (entityType: EntityProfileType) => boolean;
}

export type SubtypeItem = {
    key: AccountSubtype;
    if?: (entityType: EntityProfileType) => boolean;
} & (
    | {
          accounts: ChartOfAccountsItemOptionalNew[];
          accountsAreAutomaticallyGenerated?: false;
      }
    | {
          accountsAreAutomaticallyGenerated: true;
          accounts?: never;
      }
);

export interface ChartOfAccountsTypeWithDynamicNumberingNew {
    typeKey: AccountType;
    startFrom: number;
    upTo: number;
    endFrom: number;
    offset: number;
    offsetEndFrom?: (index: number) => number;
    subtypesStartFromBeginning: SubtypeItem[];
    subtypesStartFromEnd: SubtypeItem[];
    accountClass: AccountClass;
}

export type ChartOfAccountsTypesWithDynamicNumberingTypeNew =
    ChartOfAccountsTypeWithDynamicNumberingNew[];
